import type { DeepReadonly, MaybeRefOrGetter } from "vue";

import { nanoid } from "nanoid";
import { mapValues, sift } from "radash";
import { computed, toValue, useId } from "vue";

function useAriaIds<
  const Texts extends DeepReadonly<
    Record<string, MaybeRefOrGetter<string | null | undefined>>
  >,
>(
  texts: Texts,
  existingString?: MaybeRefOrGetter<Readonly<string | undefined>>,
) {
  const ids = mapValues(texts, (_value, key: keyof Texts) => {
    const id = useId() || nanoid(10);
    // We access the texts object directly to add it to the computed dependencies in case it's a reactive object
    return computed(() => (toValue(texts[key])?.trim() ? id : undefined));
  });

  const aria = computed(() => {
    const idString =
      sift(Object.values(ids).map(toValue)).join(" ") || undefined;
    const existingStringValue = toValue(existingString);
    if (idString && existingStringValue) {
      return `${idString} ${existingStringValue}`;
    }
    if (existingStringValue) {
      return existingStringValue;
    }
    return idString;
  });

  return { ids, aria };
}

export { useAriaIds };
